import React from "react";
import { getOrgConfig } from "../../utils/organizationConfigUtil";

/*
 * Matrices and guidelines are per-company documents, so both columns are
 * rendered straight from the resolved org's `guidelineMatrices` block in
 * src/config/organizations.js - see the comment there for the item shapes.
 * Nothing on this page should branch on which company is running.
 */

const pageStyle = {
    width: '100%',
    fontFamily: 'ProximaNova-Regular, Helvetica, Arial, SansSerifFLF, sans-serif',
    color: '#23272f',
    marginTop: '40px',
};

const cardStyle = {
    background: '#fff',
    borderRadius: '24px',
    boxShadow: '0 2px 16px #0001',
    padding: '32px',
    minWidth: '420px',
    marginTop: 16,
};

const docIcon = (
    <img
        src="/content/Images/guidelinedoc.svg"
        alt="doc"
        style={{ marginRight: "12px", width: 26, height: 26, display: 'inline-block', verticalAlign: 'middle' }}
    />
);

const DocLink = ({ label, url }) => (
    <a href={url} target="_blank" rel="noopener noreferrer" className="matrix-link" style={{ fontWeight: 600 }}>
        {label}
    </a>
);

// A row is a link when the item carries a url and plain bold text when it does
// not - which is what lets a section head a list of sub-documents without
// pointing at a PDF of its own.
const DocRow = ({ item }) => {
    if (!item.subItems?.length) {
        return (
            <li style={{ margin: "12px 0", display: "flex", alignItems: "center" }}>
                {docIcon}
                <DocLink label={item.label} url={item.url} />
            </li>
        );
    }

    return (
        <li style={{ margin: "12px 0", display: "flex", flexDirection: "column" }}>
            <span style={{ display: "flex", alignItems: "center" }}>
                {docIcon}
                {item.url
                    ? <DocLink label={item.label} url={item.url} />
                    : <span style={{ fontWeight: "bold", color: "#23272f" }}>{item.label}</span>}
            </span>
            <ul
                style={{
                    marginLeft: "32px",
                    marginTop: "4px",
                    color: "#444",
                    listStyle: item.url ? 'disc' : 'none',
                    paddingLeft: item.url ? '20px' : undefined,
                }}
            >
                {item.subItems.map((sub) => (
                    <li key={sub} style={{ fontSize: "0.98rem" }}>{sub}</li>
                ))}
            </ul>
        </li>
    );
};

const Section = ({ section }) => (
    <div key={section.title}>
        <h3 style={{ fontWeight: "bold", fontSize: "1.2rem" }}>{section.title}</h3>
        <ul style={{ listStyle: "none", padding: 0, color: '#23272f' }}>
            {section.items.map((item) => <DocRow key={item.label} item={item} />)}
        </ul>
    </div>
);

const Panel = ({ title, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 520, paddingTop: '32px' }}>
        <h2 style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '2.5rem', margin: 0, letterSpacing: 0.5, lineHeight: 1.1 }}>
            {title}
        </h2>
        <div style={cardStyle}>{children}</div>
    </div>
);

const Matrices = () => {
    const { guidelineMatrices, companyName } = getOrgConfig();
    const matrices = guidelineMatrices?.matrices ?? [];
    const guidelines = guidelineMatrices?.guidelines ?? [];

    if (!matrices.length && !guidelines.length) {
        return (
            <div style={pageStyle}>
                <p style={{ textAlign: 'center', paddingTop: '32px', color: '#4b5563' }}>
                    Matrices and guidelines for {companyName} are not published here yet.
                </p>
            </div>
        );
    }

    return (
        <div style={pageStyle}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginBottom: 32 }}>
                {matrices.length > 0 && (
                    <Panel title="Matrices">
                        {/*
                         * Matrix sections read across: occupancy over the columns, products
                         * down them. Capped at two columns so an org with more sections than
                         * Champions' two wraps onto a second row instead of stretching the
                         * card past the viewport; one section takes a single column rather
                         * than sitting beside an empty one.
                         */}
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(${Math.min(2, matrices.length)}, minmax(0, 1fr))`,
                                gap: '40px',
                                marginTop: '24px',
                            }}
                        >
                            {matrices.map((section) => <Section key={section.title} section={section} />)}
                        </div>
                    </Panel>
                )}
                {guidelines.length > 0 && (
                    <Panel title="Guidelines">
                        <div style={{ marginTop: '24px' }}>
                            {guidelines.map((section) => <Section key={section.title} section={section} />)}
                        </div>
                    </Panel>
                )}
            </div>
        </div>
    );
};

export default Matrices;
